import { createTheme } from '@mui/material/styles';

/**
 * Melsoft CyberGuard — Design Tokens
 * ------------------------------------------------------------
 * Built for an always-on SOC console, not a marketing surface.
 * Color is functional: severity levels are the only place hue
 * carries meaning beyond brand identity.
 */
export const tokens = {
  bg: {
    default: '#0A0E17',
    surface: '#111827',
    elevated: '#17202E',
    overlay: '#1E2A3D'
  },
  border: 'rgba(230, 240, 246, 0.08)',
  borderStrong: 'rgba(230, 240, 246, 0.16)',
  text: {
    primary: '#EAF0F6',
    secondary: '#93A0B4',
    muted: '#5C6B82'
  },
  brand: {
    primary: '#07182D',
    navy: '#0B2744',
    main: '#16B8D4',
    cyan: '#22C7E8',
    teal: '#13A89E',
    dim: '#116A7B',
    glow: 'rgba(22, 184, 212, 0.18)',
    gradient: 'linear-gradient(135deg, #07182D 0%, #0B2744 55%, #07566B 100%)'
  },
  severity: {
    critical: '#F5455C',
    high: '#FF8B45',
    medium: '#F6C445',
    low: '#4CD97B',
    info: '#5B8DEF'
  }
};

export const severityColor = (level) => {
  switch ((level || '').toLowerCase()) {
    case 'critical': return tokens.severity.critical;
    case 'high': return tokens.severity.high;
    case 'medium': return tokens.severity.medium;
    case 'low': return tokens.severity.low;
    default: return tokens.severity.info;
  }
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: tokens.bg.default,
      paper: tokens.bg.surface
    },
    primary: {
      main: tokens.brand.main,
      dark: tokens.brand.dim,
      contrastText: '#03131D'
    },
    error: { main: tokens.severity.critical },
    warning: { main: tokens.severity.high },
    info: { main: tokens.severity.info },
    success: { main: tokens.severity.low },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary
    },
    divider: tokens.border
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 },
    h4: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, letterSpacing: 0.2 },
    subtitle2: { color: tokens.text.secondary },
    body2: { color: tokens.text.secondary },
    overline: { fontFamily: '"JetBrains Mono", monospace', letterSpacing: 1.2, fontWeight: 500 },
    button: { fontWeight: 600, textTransform: 'none' }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg.default,
          scrollbarColor: `${tokens.borderStrong} transparent`
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: tokens.borderStrong,
          borderRadius: 8
        },
        ':focus-visible': {
          outline: `2px solid ${tokens.brand.main}`,
          outlineOffset: 2
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.bg.surface,
          border: `1px solid ${tokens.border}`
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, minHeight: 42 },
        containedPrimary: {
          boxShadow: '0 8px 24px rgba(22, 184, 212, 0.18)',
          '&:hover': { boxShadow: `0 0 0 1px ${tokens.brand.main}, 0 10px 28px rgba(22, 184, 212, 0.24)` }
        }
      }
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } } }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontFamily: '"JetBrains Mono", monospace', fontWeight: 500, fontSize: '0.72rem' }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.border }
      }
    }
  }
});

export default theme;
